import React, { useState, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import { Col, Form, Row, Modal, Card, Spinner, Tabs, Tab, Button, Table, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faCog, faTrash, faPlus, faEye } from '@fortawesome/free-solid-svg-icons';
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { WindowPanel } from "../../WindowPanel";
import ApiDataService from '../../services/ApiDataService';
import http from '../../services/http-common';
import { SnapBarError } from "../../ConfirmationDialog";
import Wysiwyg from "../../Plugin/Wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { compose } from 'redux';


const url = `${process.env.REACT_APP_SERVER_URL}admin/portal/translation/page_lov`;
const langurl = `${process.env.REACT_APP_SERVER_URL}admin/portal/translation/lang/lov`;
const bulkoption = `${process.env.REACT_APP_SERVER_URL}admin/portal/translation/bulkoption`;
const pageurl = `admin/portal/translation/index`;
const countryLovUrl = `admin/portal/seo/country_lov`;
const limit = 20;

export const getTranslationRowKey = (row, { preferParent = false } = {}) => {
  if (!row) return null;
  const candidates = preferParent
    ? [row.hp_parent_id, row.parent_id, row.shp_parent_id, row.ref_id, row.shp_id, row.sys_id, row.id]
    : [row.shp_id, row.sys_id, row.hp_id, row.id, row.ref_id, row.hp_parent_id, row.parent_id];

  for (const v of candidates) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
};

export const buildRowsById = (rows = []) => {
  const map = new Map();
  (rows || []).forEach((r) => {
    const id = getTranslationRowKey(r, { preferParent: false });
    const parentId = getTranslationRowKey(r, { preferParent: true });
    if (id) map.set(id, r);
    if (parentId) map.set(parentId, r);
  });
  return map;
};

export const buildRowsByShpId = (rows = []) => {
  const map = new Map();
  (rows || []).forEach((r) => {
    const id = (r && r.shp_id !== undefined && r.shp_id !== null) ? String(r.shp_id).trim() : '';
    if (id) map.set(id, r);
  });
  return map;
};

export const selectRowsForCountry = (rows = [], selectedCountryCode) => {
  const cc = selectedCountryCode ? String(selectedCountryCode).trim().toUpperCase() : '';
  const isAll = !cc || cc === 'ALL';
  if (isAll) return rows || [];

  const groups = new Map();
  (rows || []).forEach((r) => {
    const id = getTranslationRowKey(r, { preferParent: false });
    if (!id) return;
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id).push(r);
  });

  const normalize = (value) => {
    if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
    if (!value) return [];
    return String(value).split(',').map((v) => v.trim()).filter(Boolean);
  };

  const pickBest = (list) => {
    const exact = list.find((r) => normalize(r?.shp_applicable_countries).some((c) => String(c).toUpperCase() === cc));
    if (exact) return exact;
    const all = list.find((r) => normalize(r?.shp_applicable_countries).some((c) => String(c).toUpperCase() === 'ALL'));
    if (all) return all;
    const empty = list.find((r) => normalize(r?.shp_applicable_countries).length === 0);
    if (empty) return empty;
    return list[0];
  };

  return Array.from(groups.values()).map(pickBest).filter(Boolean);
};

export const alignTranslationRows = (enRows = [], langRows = [], orderedIds = null) => {
  const ids = Array.isArray(orderedIds) ? orderedIds.map((v) => String(v).trim()).filter(Boolean) : null;
  if (ids && ids.length) {
    const enById = buildRowsByShpId(enRows);
    const langById = buildRowsByShpId(langRows);

    const alignedEnRows = ids.map((id) => enById.get(id) || { shp_id: id });
    const alignedLangRows = ids.map((id, idx) => {
      const existing = langById.get(id);
      if (existing) return existing;
      const enRow = alignedEnRows[idx] || {};
      return {
        shp_id: id,
        hp_parent_id: enRow?.hp_parent_id ?? null,
        shp_applicable_countries: enRow?.shp_applicable_countries,
        hp_desc_type: enRow?.hp_desc_type,
        hp_link_title_type: enRow?.hp_link_title_type,
        hp_html_type: enRow?.hp_html_type,
        hp_desc: '',
        hp_link_title: '',
        hp_html: '',
        hp_link_url: '',
        hp_slug_url: ''
      };
    });

    return { alignedEnRows, alignedLangRows };
  }

  const langByKey = new Map();
  (langRows || []).forEach((r) => {
    const keys = [
      getTranslationRowKey(r, { preferParent: false }),
      getTranslationRowKey(r, { preferParent: true })
    ].filter(Boolean);
    keys.forEach((k) => langByKey.set(k, r));
  });

  const alignedLangRows = (enRows || []).map((enRow) => {
    const enKey = getTranslationRowKey(enRow, { preferParent: false }) || getTranslationRowKey(enRow, { preferParent: true });
    const existing = enKey ? langByKey.get(enKey) : null;
    if (existing) return existing;

    const parentId = enKey || ((enRow && (enRow.shp_id ?? enRow.sys_id ?? enRow.id ?? enRow.hp_id)) ? String(enRow.shp_id ?? enRow.sys_id ?? enRow.id ?? enRow.hp_id) : '');
    return {
      shp_id: parentId,
      hp_parent_id: parentId,
      shp_applicable_countries: enRow?.shp_applicable_countries,
      hp_desc_type: enRow?.hp_desc_type,
      hp_link_title_type: enRow?.hp_link_title_type,
      hp_html_type: enRow?.hp_html_type,
      hp_desc: '',
      hp_link_title: '',
      hp_html: '',
      hp_link_url: '',
      hp_slug_url: ''
    };
  });

  return { alignedEnRows: enRows, alignedLangRows };
};

const Translation = () => {
  const [val, setValue] = useState('');
  const defaultLanguage = { code: 'en', desc: 'English' };
  const [lang, setLang] = useState(defaultLanguage);
  const defaultCountry = { value: '', label: 'All Countries' };
  const [country, setCountry] = useState(defaultCountry);
  const [countryLov, setCountryLov] = useState([]);
  const [countryOptions, setCountryOptions] = useState([defaultCountry]);
  const [loading, setLoading] = useState(false);
  const [editorStatesView, setEditorViewStates] = useState([]);
  const [editorStates, setEditorStates] = useState([]);
  let [page_no, setPageNo] = useState(1);
  let [searchVal, setSearchVal] = useState('');
  let [totalPage, setTotalPage] = useState(1);
  const [snap, setSnap] = useState({
    error: '',
    snapopen: false,
    snapcolor: '',
  });

  const [state, setState] = useState([]);
  const [arState, setArState] = useState([]);
  const [enState, setEnState] = useState([]);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const selectPortalStyles = {
    container: (base) => ({ ...base, position: 'relative' }),
    menuPortal: (base) => ({ ...base, zIndex: 11000 }),
    menu: (base) => ({ ...base, zIndex: 11000 })
  };

  const getLangCode = (l) => {
    if (!l) return '';
    const raw = (typeof l === 'string') ? l : (l.code || l.lang_code || l.lang_cd || l.language_code || l.value || '');
    return String(raw).trim().split(/[\s-]+/)[0];
  };

  const getLangDesc = (l) => {
    if (!l) return '';
    if (typeof l === 'string') return l;
    return String(l.desc || l.lang_desc || l.language_name || l.label || '').trim();
  };

  useEffect(() => {
    http.get(countryLovUrl, { params: { channel_id: '101-00000018' } })
      .then((response) => {
        if (response?.data?.result && Array.isArray(response.data.result)) {
          setCountryLov(response.data.result);
        } else {
          setCountryLov([]);
        }
      })
      .catch(() => {
        setCountryLov([]);
      });
  }, []);

  useEffect(() => {
    const normalized = (countryLov || [])
      .map((c) => {
        const value = c?.ref_cn_iso || c?.country_iso || c?.ref_iso || c?.iso_code || c?.code;
        const label = c?.code || c?.country_code || c?.ref_cn_iso || value;
        return value ? { value: String(value), label: String(label) } : null;
      })
      .filter(Boolean);

    const unique = new Map();
    unique.set(defaultCountry.value, defaultCountry);
    normalized.forEach((o) => unique.set(o.value, o));
    setCountryOptions(Array.from(unique.values()));
  }, [countryLov]);

  const loadCountryOptions = async (inputValue) => {
    const options = countryOptions || [defaultCountry];
    const q = (inputValue || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const value = (o.value || '').toLowerCase();
      const label = (o.label || '').toLowerCase();
      return value.includes(q) || label.includes(q);
    });
  };

  const normalizeApplicableCountries = (value) => {
    if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
    if (!value) return [];
    return String(value)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const selectedCountryCode = (country && country.value !== undefined) ? String(country.value || '') : String(defaultCountry.value || '');
  const isAllCountries = (code) => {
    if (!code) return true;
    return String(code).toUpperCase() === 'ALL';
  };
  const getApplicableCountriesFromRows = (row, fallbackRow) => {
    const val = (row && row.shp_applicable_countries) ? row.shp_applicable_countries : (fallbackRow ? fallbackRow.shp_applicable_countries : null);
    return normalizeApplicableCountries(val);
  };
  const isRowForCountry = (row, fallbackRow = null) => {
    if (isAllCountries(selectedCountryCode)) return true;
    const countries = getApplicableCountriesFromRows(row, fallbackRow);
    if (countries.length === 0) return true;
    const selected = selectedCountryCode.toUpperCase();
    return countries.some((c) => String(c).toUpperCase() === selected || String(c).toUpperCase() === 'ALL');
  };



  const loadOptions = async (val) => {
    const itemFetch = await ApiDataService.get(`${url}?search=${val}`)
      .then(response => {
        return response.data.result;
      });
    return itemFetch;
  };

  const loadLanguage = async () => {
    const itemFetch = await ApiDataService.get(`${langurl}`)
      .then(response => {
        return response.data.result;
      });
    return itemFetch;
  };

  const loadPageData = async (langCode, current_page = 1, search = searchVal, country_code = selectedCountryCode) => {
    const selectedPage = val?.value;
    if (!selectedPage) return;
    if (!langCode) return;

    try {
      setLoading(true);
      setPageNo(parseInt(current_page));
      setState([]);
      setArState([]);
      setEnState([]);
      setEditorStates([]);
      setEditorViewStates([]);

      const cc = country_code ? String(country_code) : '';
      let url = `${pageurl}?page=${selectedPage}&lang=${encodeURIComponent(String(langCode))}&page_no=${encodeURIComponent(String(current_page))}&limit=${encodeURIComponent(String(limit))}&search=${encodeURIComponent(search || '')}`;
      if (!isAllCountries(cc)) {
        url += `&cn_iso=${encodeURIComponent(cc)}`;
      }
      const response = await ApiDataService.get(url);

      setIsLoadMore(false);

      const result = response?.data?.result;
      const rawEnRows = Array.isArray(result?.en_data?.result) ? result.en_data.result : [];
      const rawLangRows = Array.isArray(result?.lang_data?.result) ? result.lang_data.result : [];
      const isEnglish = String(langCode).toLowerCase() === 'en';
      const enRows = selectRowsForCountry(rawEnRows, cc);
      const langRows = selectRowsForCountry(isEnglish ? rawEnRows : rawLangRows, cc);

      const orderedIds = Array.isArray(result?.en_data?.table_sys_id)
        ? result.en_data.table_sys_id
        : (Array.isArray(result?.lang_data?.table_sys_id) ? result.lang_data.table_sys_id : null);
      const { alignedEnRows, alignedLangRows } = alignTranslationRows(enRows, langRows, orderedIds);

      setEnState(alignedEnRows);
      setArState(alignedLangRows);
      setState(alignedLangRows.map((row) => ({
        desc: row?.hp_desc ?? '',
        html: row?.hp_html ?? '',
        link_url: row?.hp_link_url ?? '',
        slug_url: row?.hp_slug_url ?? '',
        link_title: row?.hp_link_title ?? '',
        ...row
      })));
      setEditorStates(alignedLangRows.map((row) => row?.hp_html ?? ''));

      const count = Number(result?.en_data?.count ?? alignedEnRows?.length ?? 0);
      if (count >= limit) {
        setTotalPage(Math.max(1, Math.ceil(count / limit)));
      } else {
        setTotalPage(1);
      }
    } catch (e) {
      setSnap({ error: e?.message || 'Failed to load translation data', snapopen: true, snapcolor: '#ff4c4ceb' });
      setTotalPage(1);
      setEnState([]);
      setArState([]);
      setState([]);
      setEditorStates([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (val) {
      setEditorViewStates([]);
      setEditorStates([]);
      setState([]);
      setArState([]);
      setEnState([]);
      setPageNo(1);
      setLang(defaultLanguage);
      setSearchVal('');
    }
  }, [val]);
  useEffect(() => {
    const langCode = getLangCode(lang);
    if (!langCode) return;
    if (!val?.value) return;
    setPageNo(1);
    loadPageData(langCode, 1, searchVal, selectedCountryCode);
  }, [lang]);

  const handleInputChange = (e) => {
    const { dataset: { key }, value, name } = e.target;
    setState(prevState => ({
      ...prevState,
      [key]: { ...prevState[key], [name]: value }
    }));
  };

  const handleSubmit = (column_type, column_value, shp_id, hp_parent_id = '') => {
    //console.log(column_type, column_value, shp_id, hp_parent_id, lang.code);

    const langCode = getLangCode(lang) || 'en';
    let url = `admin/portal/translation/update/${shp_id}?lang=${encodeURIComponent(langCode)}`;
    var formData = new FormData();
    formData.append('column_type', column_type);
    formData.append('sys_id', shp_id);
    // base64-encode: raw HTML (<span style=...>) trips Akamai's XSS-SmartDetect
    // WAF rule on this endpoint. Backend decodes when value_encoding=base64.
    formData.append('column_value', btoa(unescape(encodeURIComponent(column_value ?? ''))));
    formData.append('value_encoding', 'base64');
    formData.append('parent_id', hp_parent_id);
    //formData.append('state', JSON.stringify(state));

    ApiDataService.update(url, formData, langCode).then(response => {
      if (response.data.return_status !== "0") {
        if (response.data.error_message === 'Error') {
          setSnap({ error: response.data.result, snapopen: true, snapcolor: '#ff4c4ceb' });
        } else {
          setSnap({ error: response.data.error_message, snapopen: true, snapcolor: '#ff4c4ceb' });
        }
      } else {
        setSnap({ error: response.data.error_message, snapopen: true, snapcolor: '#20bb20eb' });
      }
    }).catch((error) => {
      console.log(error);
      setSnap({ error: error.message, snapopen: true, snapcolor: '#ff4c4ceb' });
    });
  }

  const snapclose = () => {
    setSnap({ snapopen: false });
  }


  const onEditorStateChange = (editorState, i) => {
    const newEditorStates = [...editorStates];
    newEditorStates[i] = editorState;
    setEditorStates(newEditorStates);
  };

  function countWords(str) {
    if (str && str.length > 0) {
      var matches = str.trim().match(/\S+/g);
      return matches ? matches.length : 0;
    } else {
      return 0;
    }
  }

  const baseLen = Math.max(enState?.length || 0, arState?.length || 0);
  const filteredIndexes = Array.from({ length: baseLen }, (_, index) => index).filter((index) => {
    const arRow = arState && arState[index] ? arState[index] : null;
    const enRow = enState && enState[index] ? enState[index] : null;
    return isRowForCountry(arRow, enRow);
  });

  const totalWordCount = () => {
    let word_cont = 0;

    filteredIndexes.forEach((idx) => {
      const row = (enState && enState[idx]) ? enState[idx] : {};
      let { hp_desc, hp_html, hp_link_title } = row;
      word_cont += countWords(hp_desc) + countWords(hp_link_title) + countWords(hp_html);
    });
    return word_cont;

  }

  return (
    <>
      <SnapBarError
        message={snap.error}
        snapopen={snap.snapopen}
        snapcolor={snap.snapcolor}
        snapclose={snapclose}
      />

      <WindowPanel rawHtml={
        <div className="windowContent translation-page">
          <Row className="translation-filters">
            <Col className="translation-filters__item translation-filters__page">
              <Form.Label>Select your page</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                value={val}
                getOptionLabel={e => e.label}
                getOptionValue={e => e.value}
                loadOptions={loadOptions}
                onChange={setValue}
                classNamePrefix="translationSelect"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
              />
            </Col>
            {/* <Col md={4}>
              <Form.Label>Select Bulk Option</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                value={lang}
                getOptionLabel={e => e.desc}
                getOptionValue={e => e.code}
                loadOptions={loadBulkOption}
                onChange={setbulkOption}
              />
            </Col> */}
            <Col className="translation-filters__item translation-filters__lang">
              <Form.Label>Select your Language</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                value={lang}
                getOptionLabel={(e) => getLangDesc(e)}
                getOptionValue={(e) => getLangCode(e)}
                loadOptions={loadLanguage}
                onChange={setLang}
                classNamePrefix="translationSelect"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
              />
            </Col>

            <Col className="translation-filters__item translation-filters__country">
              <Form.Label>Select your Country</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions={countryOptions}
                value={country}
                getOptionLabel={(e) => e?.label ?? ''}
                getOptionValue={(e) => e?.value ?? ''}
                loadOptions={loadCountryOptions}
                onChange={(v) => {
                  const nextCountry = v || defaultCountry;
                  setCountry(nextCountry);
                  const langCode = getLangCode(lang);
                  if (val?.value && langCode) {
                    setPageNo(1);
                    loadPageData(langCode, 1, searchVal, nextCountry.value);
                  }
                }}
                classNamePrefix="translationSelect"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
              />
            </Col>

            {totalPage > 1 ?
              <Col className="translation-filters__item translation-filters__pageno">
                <div>Page No.</div>
                <select className="translation-page-no" onChange={(e) => { setPageNo(e.target.value); loadPageData(getLangCode(lang), e.target.value, searchVal, selectedCountryCode); }} defaultValue={1}>
                  {Array.from(Array(totalPage), (e, i) => {
                    let sr_no = i + 1;
                    return <option key={i} value={sr_no} selected={page_no == sr_no ? true : false}>{sr_no}</option>

                  })}
                </select>

              </Col>
              : ""}
            <Col className="translation-filters__item translation-filters__search">
              <label style={{ paddingBottom: "5px" }}>Total Word :</label>
              <b style={{ paddingBottom: "5px" }}> {enState && enState.length ? totalWordCount() : 0}</b>
              <div>
                <Form.Control size="sm" type="text" placeholder="Search" id="standard-full-width" value={searchVal} onChange={(e) => { setSearchVal(e.target.value); loadPageData(getLangCode(lang), 1, e.target.value, selectedCountryCode) }} />
              </div>
            </Col>

          </Row>
          {loading &&
            (<Row>
              <Col className="my-5" sm={{ offset: 6 }} md={{ offset: 6 }}><Spinner animation="border" size="xl" /> </Col>
            </Row>)}
          <Row className="my-5">
            {filteredIndexes.length > 0 && (
              <Col md={6}>
                <Row><Col><strong>Reference from English language content</strong></Col></Row>
                {filteredIndexes.map((index) => {
                  const enRow = (enState && enState[index]) ? enState[index] : {};
                  const arRow = (arState && arState[index]) ? arState[index] : {};

                  const hp_desc_type = (enRow.hp_desc_type ?? arRow.hp_desc_type);
                  const hp_link_title_type = (enRow.hp_link_title_type ?? arRow.hp_link_title_type);
                  const hp_html_type = (enRow.hp_html_type ?? arRow.hp_html_type);

                  const hp_desc = enRow.hp_desc;
                  const hp_link_title = enRow.hp_link_title;
                  const hp_html = enRow.hp_html;
                  return (
                    <Col key={index}>
                      {(hp_desc_type && hp_desc_type?.split('-')[0] == 'VARCHAR2') ?
                        <Form.Group as={Row} className="mb-3" controlId="exampleForm.ControlInput1">
                          <Form.Label className="mb-3">Description
                            (<span>{hp_desc && hp_desc.length ? countWords(hp_desc) : 0}</span>)
                          </Form.Label>
                          <Form.Control type="text" name="" className='hp_desc' placeholder="Description" value={hp_desc ?? ''} readOnly />
                        </Form.Group>
                        : ''
                      }

                      {(hp_link_title_type && hp_link_title_type?.split('-')[0] == 'VARCHAR2') ?
                        <Form.Group as={Row} className="mb-3" controlId="exampleForm.ControlInput1">
                          <Form.Label className="mb-3">Link Title
                            (<span>{hp_link_title && hp_link_title.length ? countWords(hp_link_title) : 0}</span>)
                          </Form.Label>
                          <Form.Control type="text" name="" className='hp_link_title' placeholder="name@example.com" value={hp_link_title ?? ''} readOnly />
                        </Form.Group>
                        : ''
                      }

                      {(hp_html_type !== false) ?
                        <Form.Group as={Row} className="mb-3" controlId="exampleForm.ControlInput1">
                          <Col className='p-0'>
                            <Form.Label className="mb-3">Detail
                              (<span>{hp_html && hp_html.length ? countWords(hp_html) : 0}</span>)
                            </Form.Label>
                          </Col>

                          <Wysiwyg
                            name="hp_html"
                            editorLoaded={true}
                            value={hp_html ?? ''}
                            disabled={true}
                            onChange={(data) => {
                            }}
                          />

                        </Form.Group>
                        : ''}
                    </Col>

                  )
                })}
              </Col>
            )}
            {filteredIndexes.length > 0 && (
              <Col md={6}>
                <Row><Col><strong>Edit your {getLangDesc(lang)} language </strong></Col></Row>
                {filteredIndexes.map((index) => {
                  const enRow = (enState && enState[index]) ? enState[index] : {};
                  const row = (arState && arState[index]) ? arState[index] : {};
                  const st = (state && state[index]) ? state[index] : {};
                  const applicableCountries = row?.shp_applicable_countries ?? enRow?.shp_applicable_countries;

                  const hp_desc_type = (enRow.hp_desc_type ?? row.hp_desc_type);
                  const hp_link_title_type = (enRow.hp_link_title_type ?? row.hp_link_title_type);
                  const hp_html_type = (enRow.hp_html_type ?? row.hp_html_type);

                  const { shp_id, hp_parent_id, hp_html, shp_applicable_countries } = row;
                  return (
                    <Col key={index}>
                      {(hp_desc_type && hp_desc_type?.split('-')[0] == 'VARCHAR2') ?
                        <Form.Group as={Row} className="mb-3" controlId="exampleForm.ControlInput1">
                          <Col className='p-0'>
                            <Form.Label className="mb-3">Description {applicableCountries ? '(' + applicableCountries + ')' : ''}</Form.Label>
                          </Col>
                          <Col className='p-0'>
                            <Button className="btn btn-primary form-control-sm float-right btnAlign" onClick={() => handleSubmit(hp_desc_type, st.desc, shp_id, hp_parent_id)}><FontAwesomeIcon icon={faEdit} /></Button>
                          </Col>
                          <Form.Control type="text" name="desc" data-key={index} placeholder="Description" value={st.desc ?? ''} onChange={handleInputChange} />
                        </Form.Group>
                        : ''
                      }

                      {(hp_link_title_type && hp_link_title_type?.split('-')[0] == 'VARCHAR2') ?
                        <Form.Group as={Row} className="mb-3" controlId="exampleForm.ControlInput1">
                          <Col className='p-0'>
                            <Form.Label className="mb-3">Link Title {applicableCountries ? '(' + applicableCountries + ')' : ''}</Form.Label>
                          </Col>
                          <Col className='p-0'>
                            <Button className="btn btn-primary form-control-sm float-right btnAlign" onClick={() => handleSubmit(hp_link_title_type, st.link_title, shp_id)}><FontAwesomeIcon icon={faEdit} /></Button>
                          </Col>
                          <Form.Control type="text" name="link_title" data-key={index} placeholder="Link Title" value={st.link_title ?? ''} onChange={handleInputChange} />
                        </Form.Group>
                        : ''
                      }

                      {(hp_html_type !== false) ?
                        <Form.Group as={Row} className="mb-3" controlId="exampleForm.ControlInput1">
                          <Col className='p-0'>
                            <Form.Label className="mb-3">Detail {applicableCountries ? '(' + applicableCountries + ')' : ''}</Form.Label>
                          </Col>
                          <Col className='p-0'>
                            <Button className="btn btn-primary form-control-sm float-right btnAlign" onClick={() => handleSubmit(hp_html_type, editorStates[index], shp_id)}><FontAwesomeIcon icon={faEdit} /></Button>
                          </Col>

                          <Wysiwyg
                            name="hp_html"
                            editorLoaded={true}
                            lang={getLangCode(lang)}
                            onChange={(data) => {
                              onEditorStateChange(data, index)
                            }}
                            value={editorStates && editorStates[index] !== undefined ? editorStates[index] : (hp_html || '')}
                          />

                        </Form.Group>
                        : ''}
                    </Col>

                  )
                })}
              </Col>
            )}
          </Row>
          {isLoadMore && totalPage >= page_no && false &&
            <Row className="justify-content-md-center">
              <Col className="my-5" md={2}>
                <Button className="btn btn-info form-control btnAlign" onClick={() => { loadPageData(getLangCode(lang), page_no, searchVal, selectedCountryCode); }}>Load More</Button>
              </Col>
            </Row>
          }


        </div>
      } />
    </>
  );
}


export default Translation;
